import { request } from '@/lib/api'

/**
 * Пользовательские поля (тикет #50, ТЗ §5.1).
 *
 * Поля объявляются администратором и появляются без релиза — поэтому клиент не знает их
 * заранее и не может выводить типы на этапе сборки. Отсюда `unknown` в значениях: любое
 * приведение здесь было бы обещанием, которого код сдержать не может.
 */

/** Тип поля. Совпадает с `custom_field.data_type` на сервере. */
export type CustomFieldType =
  | 'string'
  | 'number'
  | 'date'
  | 'boolean'
  | 'enum'
  | 'reference'
  | 'file'

export interface CustomField {
  id: string
  code: string
  label: string
  dataType: CustomFieldType
  /** Варианты для списка выбора; для прочих типов пустой массив. */
  options: string[]
  required: boolean
  displayOrder: number
}

/** Значения полей одной записи: код поля → значение. */
export type CustomFieldValues = Record<string, unknown>

export function fetchCustomFields(
  entityType: string,
  signal?: AbortSignal,
): Promise<{ fields: CustomField[] }> {
  return request<{ fields: CustomField[] }>(`/custom-fields/${entityType}`, { signal })
}

export function declareCustomField(
  entityType: string,
  field: {
    code: string
    label: string
    dataType: CustomFieldType
    options?: string[]
    required?: boolean
    displayOrder?: number
  },
): Promise<CustomField> {
  return request<CustomField>(`/custom-fields/${entityType}`, {
    method: 'POST',
    body: {
      code: field.code,
      label: field.label,
      dataType: field.dataType,
      // Варианты нужны только списку выбора; для остальных типов сервер их не ждёт.
      options: field.dataType === 'enum' ? (field.options ?? []) : null,
      required: field.required ?? false,
      displayOrder: field.displayOrder ?? 0,
    },
  })
}

export function hideCustomField(entityType: string, fieldId: string): Promise<{ hidden: boolean }> {
  return request<{ hidden: boolean }>(`/custom-fields/${entityType}/${fieldId}`, {
    method: 'DELETE',
  })
}

export function fetchCustomFieldValues(
  entityType: string,
  entityId: string,
  signal?: AbortSignal,
): Promise<{ values: CustomFieldValues }> {
  return request<{ values: CustomFieldValues }>(
    `/custom-fields/${entityType}/values/${entityId}`,
    { signal },
  )
}

/**
 * Записывает значения.
 *
 * Отправлять надо только изменённые поля: сервер сливает документ, а не заменяет его, и
 * отправка всей карточки целиком ничего не сломает, но перезапишет значения, которых
 * пользователь не касался, — а значит затрёт чужую правку, сделанную параллельно.
 */
export function saveCustomFieldValues(
  entityType: string,
  entityId: string,
  values: CustomFieldValues,
): Promise<{ values: CustomFieldValues }> {
  return request<{ values: CustomFieldValues }>(
    `/custom-fields/${entityType}/values/${entityId}`,
    { method: 'PUT', body: values },
  )
}
